import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Truck,
  FileSpreadsheet,
  DollarSign,
  PlusCircle,
  FileText,
  Building2,
  ChevronDown,
  Search,
  ArrowLeft,
  LogOut,
  Shield,
  Menu,
  X,
  Phone,
  Mail,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { useAreaCliente } from '../../contexts/AreaClienteContext';
import { usePermissionsContext } from '../../contexts/PermissionsContext';
import { FirebaseAuthService } from '../../services/firebaseAuthService';

export function AreaClienteLayout() {
  const {
    clienteAtual,
    setClienteAtual,
    listaClientes,
    stats,
    isLoading,
    isClientLocked,
    semVinculo
  } = useAreaCliente();

  const { role } = usePermissionsContext();
  const location = useLocation();
  const navigate = useNavigate();

  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const canSwitchClient = !isClientLocked || role === 'admin' || role === 'manager';

  const filteredClientes = listaClientes.filter(c =>
    c.nome.toLowerCase().includes(clientSearch.toLowerCase()) ||
    (c.documento && c.documento.includes(clientSearch))
  );

  const navItems = [
    {
      name: 'Visão Geral',
      path: '/area-cliente',
      icon: LayoutDashboard,
      badge: null
    },
    {
      name: 'Ordens de Serviço',
      path: '/area-cliente/os',
      icon: Truck,
      badge: stats?.osEmAberto ? `${stats.osEmAberto} abertas` : null,
      badgeColor: 'bg-amber-100 text-amber-800'
    },
    {
      name: 'Medições',
      path: '/area-cliente/medicoes',
      icon: FileSpreadsheet,
      badge: stats?.medicoesEmAnalise ? `${stats.medicoesEmAnalise} em análise` : null,
      badgeColor: 'bg-blue-100 text-blue-800'
    },
    {
      name: 'Extrato Financeiro',
      path: '/area-cliente/financeiro',
      icon: DollarSign,
      badge: stats?.totalFinanceiroVencido ? 'Vencidos' : null,
      badgeColor: 'bg-red-100 text-red-800'
    },
    {
      name: 'Solicitar Frete',
      path: '/area-cliente/solicitar-frete',
      icon: PlusCircle,
      badge: 'Novo',
      badgeColor: 'bg-red-600 text-white animate-pulse'
    },
    {
      name: 'Propostas e Contratos',
      path: '/area-cliente/propostas',
      icon: FileText,
      badge: stats?.totalPropostas ? `${stats.totalPropostas}` : null,
      badgeColor: 'bg-gray-100 text-gray-700'
    }
  ];

  const handleLogout = async () => {
    await FirebaseAuthService.logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col">
      {/* Top Header */}
      <header className="bg-slate-900 text-white border-b border-slate-800 sticky top-0 z-40 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            
            {/* Logo & Portal Branding */}
            <div className="flex items-center space-x-3">
              <Link to="/area-cliente" className="flex items-center space-x-2 group">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-red-600 to-red-500 flex items-center justify-center text-white font-black text-xl shadow-lg shadow-red-600/30 group-hover:scale-105 transition-transform">
                  V
                </div>
                <div>
                  <div className="flex items-center space-x-1.5">
                    <span className="font-extrabold tracking-tight text-white text-lg">VOOPE</span>
                    <span className="text-xs px-2 py-0.5 bg-red-600/30 text-red-400 border border-red-500/40 rounded-full font-medium">
                      Portal do Cliente
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-400 font-medium">Transportes & Logística</p>
                </div>
              </Link>
            </div>

            {/* Client Selector (for Admin/Staff) or Current Client Badge */}
            <div className="hidden md:flex items-center space-x-4">
              {clienteAtual && (
                <div className="relative">
                  {canSwitchClient ? (
                    <button
                      onClick={() => setClientDropdownOpen(!clientDropdownOpen)}
                      className="flex items-center space-x-3 bg-slate-800/90 hover:bg-slate-800 border border-slate-700/80 px-3.5 py-1.5 rounded-lg text-left transition-all hover:border-slate-600 shadow-sm"
                    >
                      <div className="p-1.5 bg-red-600/20 text-red-400 rounded-md">
                        <Building2 className="w-4 h-4" />
                      </div>
                      <div className="max-w-[240px]">
                        <p className="text-xs font-semibold text-white truncate">{clienteAtual.nome}</p>
                        <p className="text-[10px] text-slate-400 truncate">
                          {clienteAtual.documento ? `CNPJ/CPF: ${clienteAtual.documento}` : 'Cliente Selecionado'}
                        </p>
                      </div>
                      <ChevronDown className="w-4 h-4 text-slate-400 ml-1" />
                    </button>
                  ) : (
                    <div className="flex items-center space-x-3 bg-slate-800/60 border border-slate-700/60 px-3.5 py-1.5 rounded-lg text-left">
                      <div className="p-1.5 bg-red-600/20 text-red-400 rounded-md">
                        <Building2 className="w-4 h-4" />
                      </div>
                      <div className="max-w-[240px]">
                        <p className="text-xs font-semibold text-white truncate">{clienteAtual.nome}</p>
                        <p className="text-[10px] text-slate-400 truncate">
                          {clienteAtual.documento ? `CNPJ/CPF: ${clienteAtual.documento}` : 'Cliente Autenticado'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Client Selector Dropdown */}
                  {clientDropdownOpen && canSwitchClient && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setClientDropdownOpen(false)}
                      />
                      <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden text-slate-900 animate-in fade-in zoom-in-95 duration-100">
                        <div className="p-3 bg-slate-50 border-b border-slate-200">
                          <p className="text-xs font-bold text-slate-700 mb-2">Alternar Visualização de Cliente</p>
                          <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
                            <input
                              type="text"
                              value={clientSearch}
                              onChange={(e) => setClientSearch(e.target.value)}
                              placeholder="Buscar por nome ou CNPJ..."
                              className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                              autoFocus
                            />
                          </div>
                        </div>

                        <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                          {filteredClientes.length === 0 ? (
                            <div className="p-4 text-center text-xs text-slate-500">
                              Nenhum cliente encontrado
                            </div>
                          ) : (
                            filteredClientes.map((c) => {
                              const isSelected = clienteAtual?.id === c.id;
                              return (
                                <button
                                  key={c.id}
                                  onClick={() => {
                                    setClienteAtual(c);
                                    setClientDropdownOpen(false);
                                  }}
                                  className={`w-full text-left p-3 text-xs transition-colors flex items-center justify-between hover:bg-red-50 ${
                                    isSelected ? 'bg-red-50/80 font-semibold text-red-900' : 'text-slate-700'
                                  }`}
                                >
                                  <div className="truncate pr-2">
                                    <p className="font-medium truncate">{c.nome}</p>
                                    <p className="text-[10px] text-slate-400">{c.documento || 'Sem doc'}</p>
                                  </div>
                                  {isSelected && <CheckCircle2 className="w-4 h-4 text-red-600 shrink-0" />}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Quick Freight Request CTA */}
              <Link
                to="/area-cliente/solicitar-frete"
                className="flex items-center space-x-1.5 bg-red-600 hover:bg-red-700 text-white px-3.5 py-1.5 rounded-lg text-xs font-semibold shadow-sm shadow-red-600/30 transition-all hover:scale-105 active:scale-95"
              >
                <PlusCircle className="w-4 h-4" />
                <span>Solicitar Frete</span>
              </Link>

              {/* Return to Admin or Logout */}
              {(role === 'admin' || role === 'manager') && (
                <Link
                  to="/"
                  className="flex items-center space-x-1 text-slate-400 hover:text-white px-2.5 py-1.5 rounded-lg text-xs font-medium hover:bg-slate-800 transition-colors"
                  title="Voltar ao Painel Administrativo"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  <span>Painel Geral</span>
                </Link>
              )}

              <button
                onClick={handleLogout}
                className="text-slate-400 hover:text-red-400 p-1.5 rounded-lg hover:bg-slate-800 transition-colors"
                title="Sair do sistema"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center space-x-2">
              <Link
                to="/area-cliente/solicitar-frete"
                className="bg-red-600 text-white p-1.5 rounded-lg text-xs"
              >
                <PlusCircle className="w-5 h-5" />
              </Link>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-slate-300 hover:text-white p-1.5 rounded-lg bg-slate-800"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Secondary Navigation Tabs Bar */}
        <div className="hidden md:block bg-slate-900/95 border-t border-slate-800/80 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto flex space-x-1 overflow-x-auto py-2">
            {navItems.map((item) => {
              const isExact = location.pathname === item.path;
              const isSubPath = item.path !== '/area-cliente' && location.pathname.startsWith(item.path);
              const isActive = isExact || isSubPath;
              const Icon = item.icon;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3.5 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-red-600 text-white font-semibold shadow-sm'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800/70'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                  <span>{item.name}</span>
                  {item.badge && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${item.badgeColor}`}>
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900 border-t border-slate-800 p-4 space-y-3">
            {clienteAtual && (
              <div className="p-3 bg-slate-800/80 rounded-lg text-xs text-white border border-slate-700">
                <p className="text-slate-400 text-[10px]">Cliente Ativo:</p>
                <p className="font-bold text-white">{clienteAtual.nome}</p>
                {clienteAtual.documento && (
                  <p className="text-slate-400 text-[10px]">CNPJ: {clienteAtual.documento}</p>
                )}
              </div>
            )}

            <div className="space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium ${
                      isActive ? 'bg-red-600 text-white' : 'text-slate-300 hover:bg-slate-800'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="w-4 h-4" />
                      <span>{item.name}</span>
                    </div>
                    {item.badge && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${item.badgeColor}`}>
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>

            <div className="pt-3 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400">
              {(role === 'admin' || role === 'manager') && (
                <Link
                  to="/"
                  className="flex items-center space-x-1 text-slate-300 hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Voltar ao Painel Geral</span>
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center space-x-1 text-red-400 hover:text-red-300"
              >
                <LogOut className="w-4 h-4" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto p-3 sm:p-6 lg:p-8 pb-24 md:pb-8">
        {isLoading ? (
          <div className="min-h-[50vh] flex flex-col items-center justify-center">
            <div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-xs font-medium text-slate-500">Carregando dados da sua conta...</p>
          </div>
        ) : semVinculo ? (
          <div className="bg-white rounded-2xl p-8 text-center border border-slate-200 shadow-sm max-w-lg mx-auto mt-12">
            <Shield className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-slate-800 mb-1">Sua conta não está vinculada a nenhum cliente</h2>
            <p className="text-xs text-slate-500 mb-6">
              Não encontramos nenhuma empresa cliente associada ao e-mail da sua conta. Entre em contato com o suporte
              da Voope Transportes para vincular seu acesso corretamente.
            </p>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 hover:bg-black text-white rounded-xl text-xs font-bold transition-colors cursor-pointer"
            >
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        ) : !clienteAtual ? (
          <div className="bg-white rounded-2xl p-8 text-center border border-slate-200 shadow-sm max-w-lg mx-auto mt-12">
            <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-slate-800 mb-1">Nenhum Cliente Selecionado</h2>
            <p className="text-xs text-slate-500 mb-6">
              Selecione uma empresa cliente para acessar os módulos de Ordens de Serviço, Medições, Financeiro e Propostas.
            </p>
            {canSwitchClient && listaClientes.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-700 text-left">Escolha um cliente:</p>
                <div className="max-h-48 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg text-left">
                  {listaClientes.slice(0, 10).map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setClienteAtual(c)}
                      className="w-full p-2.5 text-xs text-left hover:bg-red-50 hover:text-red-700 transition-colors flex items-center justify-between cursor-pointer"
                    >
                      <span className="font-medium truncate">{c.nome}</span>
                      <span className="text-[10px] text-slate-400 ml-2">{c.documento}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <Outlet />
        )}
      </main>

      {/* Mobile Bottom Navigation Bar */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-slate-900/95 backdrop-blur-md border-t border-slate-800 z-40 flex items-center justify-around px-2 py-1 shadow-2xl safe-area-bottom">
        <Link
          to="/area-cliente"
          className={`flex flex-col items-center justify-center py-1.5 px-2 rounded-xl text-[10px] font-bold transition min-w-[54px] ${
            location.pathname === '/area-cliente'
              ? 'text-red-500 bg-red-950/40'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <LayoutDashboard className="w-5 h-5 mb-0.5" />
          <span className="leading-tight">Início</span>
        </Link>
        <Link
          to="/area-cliente/os"
          className={`flex flex-col items-center justify-center py-1.5 px-2 rounded-xl text-[10px] font-bold transition min-w-[54px] ${
            location.pathname.startsWith('/area-cliente/os')
              ? 'text-red-500 bg-red-950/40'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <Truck className="w-5 h-5 mb-0.5" />
          <span className="leading-tight">O.S</span>
        </Link>
        <Link
          to="/area-cliente/solicitar-frete"
          className="flex flex-col items-center justify-center py-1.5 px-2.5 rounded-xl text-[10px] font-bold bg-red-600 hover:bg-red-700 text-white shadow-md shadow-red-600/40 min-w-[62px] transition"
        >
          <PlusCircle className="w-5 h-5 mb-0.5" />
          <span className="leading-tight">Solicitar</span>
        </Link>
        <Link
          to="/area-cliente/propostas"
          className={`flex flex-col items-center justify-center py-1.5 px-2 rounded-xl text-[10px] font-bold transition min-w-[54px] ${
            location.pathname.startsWith('/area-cliente/propostas')
              ? 'text-red-500 bg-red-950/40'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <FileText className="w-5 h-5 mb-0.5" />
          <span className="leading-tight">Propostas</span>
        </Link>
        <button
          type="button"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`flex flex-col items-center justify-center py-1.5 px-2 rounded-xl text-[10px] font-bold transition min-w-[54px] cursor-pointer ${
            mobileMenuOpen
              ? 'text-red-400 bg-red-950/40'
              : 'text-slate-400 hover:text-slate-200'
          }`}
        >
          <Menu className="w-5 h-5 mb-0.5" />
          <span className="leading-tight">Menu</span>
        </button>
      </nav>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-6 text-center text-xs text-slate-500 mt-auto hidden md:block">
        <div className="max-w-7xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center space-x-2">
            <span className="font-bold text-slate-800">Voope Transportes e Cargas</span>
            <span>•</span>
            <span>Área do Cliente v2.0</span>
          </div>
          <div className="flex items-center space-x-4 text-[11px] text-slate-500">
            <span className="flex items-center gap-1">
              <Phone className="w-3 h-3 text-red-500" /> (11) 98765-4321
            </span>
            <span className="flex items-center gap-1">
              <Mail className="w-3 h-3 text-red-500" /> comercial@voopetrans.com.br
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
